import {
  ProcessStatus,
  ProcessStatusOrder,
  ClearanceItem,
  OrderItem,
  ALL_PROCESS_STATUSES,
  EARLY_STATES,
  LATE_STATES,
  ERROR_MESSAGES,
} from './processStatusTypes'
import { getStoredFilterSearch } from '../../../../lib/hooks/useOrderFilters'

/**
 * Calculate unpicked clearance items
 */
export function getUnpickedItems(clearanceItems?: ClearanceItem[] | null): ClearanceItem[] {
  if (!clearanceItems) return []
  return clearanceItems.filter(item => item.picked !== item.amount)
}

/**
 * Calculate unpacked product items
 */
export function getUnpackedItems(items?: OrderItem[] | null): OrderItem[] {
  if (!items) return []
  return items.filter(item => !item.packed && item.type === 'product')
}

/**
 * Check if process status can be updated based on current state and order data
 */
export function checkCanUpdateProcessStatus(
  currentStatus: ProcessStatus,
  unpickedItems: ClearanceItem[],
  unpackedItems: OrderItem[],
  carrierId?: string,
): boolean {
  // Cannot update if there are unpicked items in picking status
  if (currentStatus === 'picking' && unpickedItems.length > 0) {
    return false
  }

  // Cannot update if there are unpacked items in packing status
  if (currentStatus === 'packing' && unpackedItems.length > 0) {
    return false
  }

  // Cannot update if no carrier is set in packing status
  if (currentStatus === 'packing' && !carrierId) {
    return false
  }

  return true
}

/**
 * Get available back steps based on current process status
 */
export function getAvailableBackSteps(currentStatus: ProcessStatus): ProcessStatus[] {
  if (!currentStatus) return []

  const currentIndex = ALL_PROCESS_STATUSES.indexOf(currentStatus)

  if (EARLY_STATES.includes(currentStatus)) {
    return ALL_PROCESS_STATUSES.slice(0, currentIndex)
  }

  if (LATE_STATES.includes(currentStatus)) {
    // For late states, can go back to 'packed' and onwards
    const packedIndex = ALL_PROCESS_STATUSES.indexOf('packed')
    return ALL_PROCESS_STATUSES.slice(packedIndex, currentIndex)
  }

  return []
}

/**
 * Extract current process status from order
 */
export function getCurrentProcessStatus(order?: ProcessStatusOrder | null): ProcessStatus {
  return (order?.processStatus?.id as ProcessStatus) || 'recieved'
}

/**
 * Find the last working status before the order was marked as picking_error
 */
export function getStatusBeforeError(order?: ProcessStatusOrder | null): ProcessStatus {
  const relations = (order?.processStatusRelations || [])
    .filter(Boolean)
    .sort((a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime())

  const previous = relations.find(r => {
    const id = r.status?.id as ProcessStatus | undefined
    return id && id !== 'picking_error' && id !== 'cancelled' && ALL_PROCESS_STATUSES.includes(id)
  })

  return (previous?.status?.id as ProcessStatus) || 'recieved'
}

/**
 * Validate order before initializing packages
 */
export function validateOrderForPackageInit(order: ProcessStatusOrder): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!order.carrier) {
    errors.push(ERROR_MESSAGES.MISSING_CARRIER)
  }

  if (order.carrier?.requiredShippingAddress && !order.shippingAddress) {
    errors.push(ERROR_MESSAGES.MISSING_SHIPPING_ADDRESS)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Build error message for package initialization
 */
export function buildPackageInitErrorMessage(errors: string[]): string {
  let message = 'Chyba pri zakladani baliku:\n\n'

  errors.forEach(error => {
    message += `${error} \n\n`
  })

  message += ERROR_MESSAGES.COMPLETE_INFO

  return message
}

/**
 * Check if print result has errors
 */
export function hasPrintErrors(result?: { stderr?: string; error?: string }): boolean {
  if (!result) return false

  const hasStderrError = result.stderr?.length > 0 && result.stderr.includes('error')
  const hasError = result.error?.length > 0 && result.error.includes('error')

  return hasStderrError || hasError
}

/**
 * Determine redirect path based on group and current status
 */
export function getRedirectPath(currentStatus: ProcessStatus, group: string | null): string {
  if (group) {
    return `/group/${group}`
  }
  const search = getStoredFilterSearch()
  return `/dashboard/${currentStatus}${search}`
}
